import { erc20Abi, maxUint160, maxUint256, maxUint48 } from 'viem';
import { PERMIT2_ADDRESS, PERMIT2_DOMAIN_NAME, PERMIT_TYPES } from '../constants';
import { weiFromWei } from '../utils';
import { wei } from '@kwenta/wei';
export function permit2Domain(chainId) {
    return {
        name: PERMIT2_DOMAIN_NAME,
        verifyingContract: PERMIT2_ADDRESS,
        chainId,
    };
}
export default class TokensService {
    constructor(sdk) {
        this.sdk = sdk;
    }
    /**
     * @desc Get permit nonce
     * @param owner Wallet address
     * @param token ERC20 address
     * @param spender Spender address
     * @returns Permit 2 allowance data including allowance, expiry and noce
     */
    async getPermit2AllowanceData(owner, token, spender) {
        const { Permit2 } = this.sdk.context.defaultContractConfigs;
        if (!Permit2)
            throw new Error('Permit2 contract not found');
        const [amount, expiry, nonce] = await this.sdk.context.client.readContract({
            ...Permit2,
            functionName: 'allowance',
            args: [owner, token, spender],
        });
        return {
            allowance: weiFromWei(amount.toString()),
            nonce: Number(nonce.toString()),
            expiry: Number(expiry.toString()),
        };
    }
    /**
     *
     * @param sdk
     * @param tokenAddress
     * @param owner
     * @param spender
     * @param amount
     * @param deadline
     * @returns Permit2 typed data
     */
    async getPermit2TypedData(tokenAddress, owner, spender, amount, deadline) {
        const { Permit2 } = this.sdk.context.defaultContractConfigs;
        if (!Permit2)
            throw new Error('Permit2 contract not found');
        const chainId = this.sdk.context.walletClient.chain?.id;
        if (!chainId)
            throw new Error('Chain ID not set');
        const { nonce } = await this.getPermit2AllowanceData(owner, tokenAddress, spender);
        const details = {
            token: tokenAddress,
            amount: BigInt(amount ?? maxUint160),
            expiration: BigInt(deadline ?? maxUint48),
            nonce: nonce,
        };
        const message = {
            details,
            spender,
            // TODO: Set reasonable deadlines
            sigDeadline: deadline ?? maxUint48,
        };
        const domain = permit2Domain(chainId);
        return {
            domain,
            account: owner,
            primaryType: 'PermitSingle',
            types: PERMIT_TYPES,
            message: message,
        };
    }
    async approveTokenSpend(address, spender, signer, amount = maxUint256) {
        const { request } = await this.sdk.context.client.simulateContract({
            abi: erc20Abi,
            address,
            account: signer ?? this.sdk.context.walletAddress,
            functionName: 'approve',
            args: [spender, amount],
        });
        return request;
    }
    async checkAllowance(address, spender, signer) {
        const allowance = await this.sdk.context.client.readContract({
            abi: erc20Abi,
            address,
            functionName: 'allowance',
            args: [signer ?? this.sdk.context.walletAddress, spender],
        });
        return allowance;
    }
    async transferToken(address, to, amount, signer) {
        const { request } = await this.sdk.context.client.simulateContract({
            abi: erc20Abi,
            address,
            account: signer ?? this.sdk.context.walletAddress,
            functionName: 'transfer',
            args: [to, amount],
        });
        return request;
    }
    async getBalanceOf(token, owner) {
        const tokenContract = this.sdk.context.defaultContractConfigs[token];
        if (!tokenContract)
            throw new Error(`${token} not found on current network`);
        const usdcBal = await this.sdk.context.client.readContract({
            abi: tokenContract.abi,
            address: tokenContract.address,
            functionName: 'balanceOf',
            args: [owner],
        });
        const decimals = await this.sdk.context.client.readContract({
            abi: tokenContract.abi,
            address: tokenContract.address,
            functionName: 'decimals',
        });
        return wei(usdcBal ?? 0, Number(decimals));
    }
}
